import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { camerasAPI, modelsAPI, type ModelCategory, type ModelDefinition } from '../utils/api';
import { useAuth } from './AuthContext';

export interface ModelCamera {
  id: number;
  name: string;
  area_id: number;
  models: string[];
  camera_id: string | null;
}

/** Категория доступна только при точном совпадении реально подключённой модели с каталогом. */
export const cameraHasCatalogModel = (cameraModels: string[] | undefined, modelNames: Set<string>): boolean => {
  return (cameraModels || []).some((value) => {
    const key = String(value).trim().toLowerCase();
    return modelNames.has(key);
  });
};

interface ModelsContextValue {
  categories: ModelCategory[];
  models: ModelDefinition[];
  cameras: ModelCamera[];
  loading: boolean;
  refresh: () => Promise<void>;
}

const ModelsContext = createContext<ModelsContextValue | undefined>(undefined);

export const useModelsCatalog = (): ModelsContextValue => useContext(ModelsContext) ?? {
  categories: [], models: [], cameras: [], loading: false, refresh: async () => {},
};

export const ModelsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<ModelCategory[]>([]);
  const [models, setModels] = useState<ModelDefinition[]>([]);
  const [cameras, setCameras] = useState<ModelCamera[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setCategories([]); setModels([]); setCameras([]); return;
    }
    setLoading(true);
    try {
      const [catalog, cameraResponse] = await Promise.all([modelsAPI.getAll(), camerasAPI.getAll()]);
      if (catalog.success) {
        setCategories(catalog.categories || []);
        setModels(catalog.models || []);
      }
      if (cameraResponse.success) setCameras(cameraResponse.cameras || []);
    } catch {
      setCategories([]); setModels([]); setCameras([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo(() => ({ categories, models, cameras, loading, refresh }),
    [categories, models, cameras, loading, refresh]);
  return <ModelsContext.Provider value={value}>{children}</ModelsContext.Provider>;
};
